import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  Group,
  Stack,
  Table,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Center,
  Loader,
  Modal,
  Select,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconFlask,
  IconPlus,
  IconCheck,
  IconClock,
  IconMicroscope,
  IconEdit,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { isSelectableStainCode } from "../../utils/controlStainSelection";
import { SlideInsufficientTissueCheckbox } from "../workflows/SlideInsufficientTissueCheckbox";
import {
  useToggleSlideDone,
  useSpecialStains,
  useCorrectSlideCpt,
} from "../../hooks/useStainRequest";
import {
  DeleteStainButton,
  PATHOLOGIST_REVIEW_STAIN_DELETE_PERMISSIONS,
} from "../workflows";

// ==================== TYPES ====================

interface SlideRow {
  slideId: string;
  stainId: string | null;
  specimenNumber: string;
  blockNumber: string;
  slideNumber: string;
  stainCode: string;
  stainDescription: string;
  stainType: string;
  orderedByName: string | null;
  dateOrdered: string | null;
  isCompleted: boolean;
  remarks: string | null;
  isAdditional: boolean;
  status: string;
  origin: string;
  // Kept so the delete confirmation can warn that staining already started.
  stainingStartedAt: string | null;
  hl7MessageId: string | null;
  panelOrderComponentId: string | null;
}

interface StainsCenterProps {
  caseId: string | null;
  caseData: any;
  onRequestStains: () => void;
  isReadOnly?: boolean;
}

// ==================== HELPERS ====================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function flattenSlides(caseData: any): SlideRow[] {
  if (!caseData?.specimens) return [];

  const specimens = [...caseData.specimens].sort((a: any, b: any) =>
    (a.number || "").localeCompare(b.number || "")
  );

  return specimens.flatMap((specimen: any) => {
    const blocks = [...(specimen.blocks || [])].sort((a: any, b: any) =>
      (a.number || "").localeCompare(b.number || "")
    );
    return blocks.flatMap((block: any) => {
      const slides = [...(block.slides || [])].sort((a: any, b: any) => {
        const cmp = (a.number || "").localeCompare(b.number || "");
        if (cmp !== 0) return cmp;
        return (a.createdAt || "").localeCompare(b.createdAt || "");
      });
      return slides.map(
        (slide: any): SlideRow => ({
          slideId: slide.id,
          stainId: slide.stainId || slide.stain?.id || null,
          specimenNumber: specimen.number,
          blockNumber: block.number,
          slideNumber: slide.number,
          stainCode: slide.stain?.code || "—",
          stainDescription: slide.stain?.description || "",
          stainType: slide.stain?.stainType || "Other",
          orderedByName: slide.completedBy?.fullName || null,
          dateOrdered: slide.createdAt || null,
          isCompleted: slide.isCompleted || false,
          remarks: slide.requestReason || null,
          isAdditional: slide.isAdditional || false,
          status: slide.status || "",
          origin: slide.origin || "",
          stainingStartedAt: slide.stainingStartedAt || null,
          hl7MessageId: slide.hl7MessageId || null,
          panelOrderComponentId: slide.panelOrderComponentId || null,
        })
      );
    });
  });
}

// ==================== COMPONENT ====================

export function StainsCenter({
  caseId,
  caseData,
  onRequestStains,
  isReadOnly = false,
}: StainsCenterProps) {
  const [togglingSlides, setTogglingSlides] = useState<Record<string, boolean>>(
    {}
  );
  const toggleDoneMutation = useToggleSlideDone(caseId || undefined);

  const rows = useMemo(() => flattenSlides(caseData), [caseData]);

  // Stain/CPT Correction States
  const [correctionTarget, setCorrectionTarget] = useState<SlideRow | null>(
    null
  );
  const [selectedStainId, setSelectedStainId] = useState<string | null>(null);
  const [correctionReason, setCorrectionReason] = useState<string>("");
  const [correctionOpened, { open: openCorrection, close: closeCorrection }] =
    useDisclosure(false);

  // Stain/CPT Correction Hooks
  const { data: specialStains = [], isLoading: isLoadingSpecialStains } =
    useSpecialStains();
  const correctCptMutation = useCorrectSlideCpt(caseId || undefined);

  // Memoized special stains options for Select dropdown
  const specialStainsOptions = useMemo(() => {
    const options: Array<{ value: string; label: string; disabled?: boolean }> =
      specialStains
        .filter((stain) => isSelectableStainCode(stain.code))
        .map((s) => ({
          value: s.id,
          label: `${s.code} - ${s.description} (CPT ${s.cptCode?.code || "—"})`,
        }));
    if (
      correctionTarget?.stainId &&
      !options.some((option) => option.value === correctionTarget.stainId)
    ) {
      options.push({
        value: correctionTarget.stainId,
        label: `${correctionTarget.stainCode} - ${correctionTarget.stainDescription}`,
        disabled: true,
      });
    }
    return options;
  }, [specialStains, correctionTarget]);

  // Handler to open correction dialog and pre-select current variant
  const handleOpenCorrection = useCallback(
    (row: SlideRow) => {
      setCorrectionTarget(row);
      setSelectedStainId(row.stainId);
      setCorrectionReason("");
      openCorrection();
    },
    [openCorrection]
  );

  const handleOpenCorrectionClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const row = rows.find(
        (candidate) => candidate.slideId === event.currentTarget.dataset.slideId
      );
      if (row) handleOpenCorrection(row);
    },
    [rows, handleOpenCorrection]
  );
  const handleCorrectionReasonChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setCorrectionReason(event.currentTarget.value),
    []
  );

  // Handler to execute the CPT correction
  const handleCorrectCpt = useCallback(async () => {
    if (!correctionTarget || !selectedStainId || !caseId) return;
    try {
      await correctCptMutation.mutateAsync({
        slideId: correctionTarget.slideId,
        newStainId: selectedStainId,
        reason: correctionReason,
      });
      notifications.show({
        title: "Success",
        message: "Stain billing CPT code corrected successfully.",
        color: "green",
      });
      setCorrectionTarget(null);
      setSelectedStainId(null);
      setCorrectionReason("");
      closeCorrection();
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err.message || "Failed to correct billing CPT code.",
        color: "red",
      });
    }
  }, [
    correctionTarget,
    selectedStainId,
    correctionReason,
    caseId,
    correctCptMutation,
    closeCorrection,
  ]);

  const canRequestAdditionalStains = useMemo(
    () =>
      !caseData?.isBlocked &&
      (caseData?.specimens?.some((s: any) => s.blocks && s.blocks.length > 0) ??
        false),
    [caseData]
  );

  const canCompletePendingStainsAfterSignOff = useMemo(
    () => Boolean(caseData?.isFinalized && !caseData?.isBlocked),
    [caseData?.isBlocked, caseData?.isFinalized]
  );

  const handleToggleDone = useCallback(
    async (slideId: string, currentDone: boolean) => {
      setTogglingSlides((prev) => ({ ...prev, [slideId]: true }));
      try {
        await toggleDoneMutation.mutateAsync(slideId);
      } catch {
        notifications.show({
          title: "Error",
          message: `Failed to mark slide as ${
            currentDone ? "not done" : "done"
          }`,
          color: "red",
        });
      } finally {
        setTogglingSlides((prev) => {
          const next = { ...prev };
          delete next[slideId];
          return next;
        });
      }
    },
    [toggleDoneMutation]
  );

  const handleToggleDoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const slideId = event.currentTarget.dataset.slideId;
      if (!slideId) return;
      void handleToggleDone(
        slideId,
        event.currentTarget.dataset.currentDone === "true"
      );
    },
    [handleToggleDone]
  );

  if (!caseData) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (rows.length === 0) {
    return (
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconMicroscope size={18} />
            <Text fw={600} size="sm">
              STAINS
            </Text>
          </Group>
          {canRequestAdditionalStains && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={onRequestStains}
            >
              Request Stains
            </Button>
          )}
        </Group>
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconFlask size={32} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">
              No slides found for this case
            </Text>
          </Stack>
        </Center>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="xs">
          <IconMicroscope size={18} />
          <Text fw={600} size="sm">
            STAINS
          </Text>
          <Badge size="sm" variant="light" color="gray">
            {rows.length} slide{rows.length !== 1 ? "s" : ""}
          </Badge>
        </Group>
        {canRequestAdditionalStains && (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={onRequestStains}
          >
            Request Stains
          </Button>
        )}
      </Group>

      {/* Stains Grid */}
      <Box style={{ overflowX: "auto" }}>
        <Table
          withTableBorder
          withColumnBorders
          fz="sm"
          style={{ minWidth: 900 }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>ID</Table.Th>
              <Table.Th w={60}>Block</Table.Th>
              <Table.Th w={80}>Slide ID</Table.Th>
              <Table.Th w={130}>Stain</Table.Th>
              <Table.Th w={130}>Ordered By</Table.Th>
              <Table.Th w={120}>Date Ordered</Table.Th>
              <Table.Th w={60} style={{ textAlign: "center" }}>
                Done
              </Table.Th>
              <Table.Th>Remarks</Table.Th>
              <Table.Th>Insufficient tissue</Table.Th>
              <Table.Th w={42} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => {
              const isToggling = !!togglingSlides[row.slideId];

              return (
                <Table.Tr
                  key={row.slideId}
                  bg={row.isCompleted ? "green.0" : undefined}
                >
                  {/* Specimen ID */}
                  <Table.Td>
                    <Text fw={600} size="sm">
                      {row.specimenNumber}
                    </Text>
                  </Table.Td>

                  {/* Block */}
                  <Table.Td>
                    <Text size="sm">{row.blockNumber}</Text>
                  </Table.Td>

                  {/* Slide ID */}
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {row.slideNumber}
                    </Text>
                  </Table.Td>

                  {/* Stain */}
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Text size="sm" fw={500}>
                        {row.stainCode}
                      </Text>
                      {row.stainType === "IHC" && (
                        <Badge size="xs" variant="light" color="violet">
                          IHC
                        </Badge>
                      )}
                      {row.origin === "sending_client" && (
                        <Tooltip
                          label="Received already stained from the referring lab — no in-house staining performed."
                          withArrow
                          multiline
                          maw={240}
                        >
                          <Badge size="xs" variant="light" color="cyan">
                            Pre-stained
                          </Badge>
                        </Tooltip>
                      )}
                    </Group>
                    {row.stainDescription &&
                      row.stainDescription !== row.stainCode && (
                        <Text size="xs" c="dimmed" truncate>
                          {row.stainDescription}
                        </Text>
                      )}
                  </Table.Td>

                  {/* Ordered By */}
                  <Table.Td>
                    <Text
                      size="sm"
                      c={row.orderedByName ? undefined : "dimmed"}
                    >
                      {row.orderedByName || "—"}
                    </Text>
                  </Table.Td>

                  {/* Date Ordered */}
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <IconClock
                        size={12}
                        color="var(--mantine-color-dimmed)"
                      />
                      <Text size="xs" c="dimmed">
                        {formatDate(row.dateOrdered)}
                      </Text>
                    </Group>
                  </Table.Td>

                  {/* Done */}
                  <Table.Td style={{ textAlign: "center" }}>
                    {isToggling ? (
                      <Loader size="xs" />
                    ) : (
                      <Checkbox
                        checked={row.isCompleted}
                        data-slide-id={row.slideId}
                        data-current-done={row.isCompleted}
                        onChange={handleToggleDoneChange}
                        disabled={
                          isReadOnly &&
                          (!canCompletePendingStainsAfterSignOff ||
                            row.isCompleted)
                        }
                        color="green"
                        size="sm"
                      />
                    )}
                  </Table.Td>

                  {/* Remarks */}
                  <Table.Td>
                    <Text size="sm" c={row.remarks ? undefined : "dimmed"}>
                      {row.remarks || "—"}
                    </Text>
                  </Table.Td>

                  <Table.Td>
                    {caseId && (
                      <SlideInsufficientTissueCheckbox
                        caseId={caseId}
                        slideId={row.slideId}
                        name={row.slideNumber}
                        source="pathologist_review"
                        disabled={
                          (isReadOnly &&
                            !caseData.isFinalized &&
                            !caseData.isPartiallyFinalized) ||
                          caseData.isBlocked
                        }
                      />
                    )}
                  </Table.Td>

                  {/* Delete */}
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      {row.isCompleted && (
                        <Tooltip
                          label="Change CPT code / Correct billing"
                          withArrow
                        >
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="sm"
                            aria-label="Change CPT code / Correct billing"
                            data-slide-id={row.slideId}
                            onClick={handleOpenCorrectionClick}
                            disabled={isReadOnly}
                          >
                            <IconEdit size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {caseId && (
                        <DeleteStainButton
                          slide={{
                            id: row.slideId,
                            number: row.slideNumber,
                            status: row.status,
                            isCompleted: row.isCompleted,
                            stainingStartedAt: row.stainingStartedAt,
                            hl7MessageId: row.hl7MessageId,
                            panelOrderComponentId: row.panelOrderComponentId,
                            stain: { code: row.stainCode },
                          }}
                          caseId={caseId}
                          caseNumber={caseData?.caseNumber}
                          slideLabel={`${row.specimenNumber}-${row.blockNumber}-${row.slideNumber}`}
                          disabled={isReadOnly}
                          allowPostSignOffEvaluation={
                            !!caseData?.isFinalized &&
                            !caseData?.isBlocked &&
                            !caseData?.isLegacyImported
                          }
                          evaluateEligibility
                          requiredPermissions={
                            PATHOLOGIST_REVIEW_STAIN_DELETE_PERMISSIONS
                          }
                        />
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>

      {/* Legend */}
      <Group gap="xs">
        <IconCheck size={14} color="var(--mantine-color-green-6)" />
        <Text size="xs" c="dimmed">
          Done rows highlighted in green
        </Text>
        <Text size="xs" c="dimmed">
          •
        </Text>
        <Text size="xs" c="dimmed">
          Control slide traceability tracked per stain per date for inspections
        </Text>
      </Group>

      {/* Correct Billing / CPT modal */}
      <Modal
        opened={correctionOpened}
        onClose={closeCorrection}
        title="Correct Stain Billing / CPT Code"
        size="md"
        centered
      >
        <Stack gap="md">
          {correctionTarget && (
            <>
              <Text size="sm">
                Correct the billing CPT code for slide{" "}
                <Text span fw={600} ff="monospace">
                  {correctionTarget.slideNumber}
                </Text>{" "}
                (
                <Text span fw={600}>
                  {correctionTarget.stainCode}
                </Text>
                ). This will update the slide's stain association in-place
                without ordering a new slide or triggering lab work.
              </Text>

              <Select
                label="Corrected Stain Variant (CPT)"
                placeholder={
                  isLoadingSpecialStains
                    ? "Loading stains..."
                    : "Select corrected variant"
                }
                data={specialStainsOptions}
                value={selectedStainId}
                onChange={setSelectedStainId}
                disabled={isLoadingSpecialStains}
                comboboxProps={{ withinPortal: true }}
              />

              <TextInput
                label="Correction Reason"
                required
                placeholder="e.g., AB/PAS used for non-infectious evaluation (88313)"
                value={correctionReason}
                onChange={handleCorrectionReasonChange}
              />

              <Group justify="flex-end" gap="sm" mt="md">
                <Button
                  variant="outline"
                  color="gray"
                  onClick={closeCorrection}
                >
                  Cancel
                </Button>
                <Button
                  color="blue"
                  onClick={handleCorrectCpt}
                  loading={correctCptMutation.isPending}
                  disabled={!selectedStainId || !correctionReason.trim()}
                >
                  Apply Correction
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}
